import React, { useState } from 'react';
import { ChevronDown } from '@withjoy/joykit/icons';
import { forwardRef } from '@shared/utils/forwardRef';
import { useFormControl } from '../FormControl/FormControl.provider';
import { Box } from '../Box';
import type { SharedInputProps } from '../InputV2/Input.types';
import { useInputStyles } from '../InputV2/Input.styles';

export type NativeSelectOption = Readonly<{ value: string; label: string; disabled?: boolean }>;

export type NativeSelectProps = Readonly<{
  options: ReadonlyArray<NativeSelectOption>;
  /** The selected option's value. A value that matches no option shows the placeholder. */
  value: string | undefined;
  onChange: (value: string) => void;
  /** Shown while no option is selected. It can't be chosen again once an option has been. */
  placeholder?: string;
  /** Lets the browser fill the field, for example `country` or `address-level1`. */
  autoComplete?: string;
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
  'aria-label'?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
}>;

/**
 * A select backed by the browser's own `<select>`, drawn on the shared field contract (the same
 * root, ring and states as InputV2).
 *
 * Use it where the browser has to be able to fill or operate the field itself: autofill sets a
 * native select (a country from a saved address), and phones open their own picker. SelectV1 is a
 * custom listbox that the browser can neither fill nor replace, so it suits searchable or styled
 * option lists instead.
 */
export const NativeSelect = forwardRef<'select', NativeSelectProps>((props, ref) => {
  const { options, value, onChange, placeholder = '', autoComplete, id, name, isDisabled, isInvalid, isRequired } = props;
  const hasSelection = options.some(option => option.value === value);
  const [isFocused, setIsFocused] = useState(false);
  const formControlProps = useFormControl({ isDisabled, isInvalid, isRequired });

  const sharedProps: SharedInputProps = {
    $isDisabled: !!formControlProps.isDisabled,
    $isInvalid: !!formControlProps.isInvalid,
    $isFocused: isFocused,
    $isFullWidth: true,
    $isRequired: false,
    $size: 'md',
  };
  const styles = useInputStyles(sharedProps);

  return (
    <Box
      {...styles.Root}
      position="relative"
    >
      <Box
        as="select"
        ref={ref}
        id={id}
        name={name}
        value={hasSelection ? value : ''}
        autoComplete={autoComplete}
        disabled={formControlProps.isDisabled}
        required={formControlProps.isRequired}
        aria-invalid={formControlProps.isInvalid || undefined}
        aria-describedby={props['aria-describedby']}
        aria-labelledby={props['aria-labelledby']}
        aria-label={props['aria-label']}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        __css={{
          ...styles.Input,
          appearance: 'none',
          // Room for the chevron, so a long option never runs under it.
          paddingRight: 9,
          textOverflow: 'ellipsis',
        }}
      >
        {!hasSelection && (
          <option
            value=""
            disabled
          >
            {placeholder}
          </option>
        )}
        {options.map(option => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </Box>
      <Box
        aria-hidden
        position="absolute"
        right={5}
        top="50%"
        transform="translateY(-50%)"
        display="flex"
        pointerEvents="none"
      >
        <ChevronDown size={16} />
      </Box>
    </Box>
  );
});

NativeSelect.displayName = 'NativeSelect';
